import { useEffect, useState } from 'react';
import {
  Box,
  styled,
  Grid,
  InputAdornment,
  TextField,
  Autocomplete,
  Typography,
  Button,
  Paper
} from '@mui/material';
import PageHeader from '../../../components/PageHeader';
import ChartSummary from './ChartSummary';
import SearchIcon from '@mui/icons-material/Search';
import TableSummaryWorkingTime from './TableSummary';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Company } from '../../../schema/company';
import { api } from '../../../api';
import MultiSelect from '../../../components/MultiSelect';
import { PersonType } from '../../../schema/person';
import { formatDate } from '../../../util';

const SummaryWorkingTime = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [filterID, setFilterID] = useState<string>('');
  const [filterName, setFilterName] = useState<string>('');
  const [filterCompanyIDList, setFilterCompanyIDList] = useState<string[]>([]);
  const [filterPersonTypeIDList, setFilterPersonTypeIDList] = useState<string[]>([]);
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  const [filterStartDate, setFilterStartDate] = useState<Date | null>(new Date());
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [personTypes, setPersonTypes] = useState<PersonType[]>([]);

  const handleExportCSVFile = () => {
    if (isLoading) {
      return;
    }
    const searchParams = new URLSearchParams();
    if (filterID) {
      searchParams.set('personal_id', `${filterID}`);
    }
    if (filterName) {
      searchParams.set('name', `${filterName}`);
    }
    if (filterCompanyIDList.length) {
      filterCompanyIDList.forEach((company_id) => {
        searchParams.append('company_id', `${company_id}`);
      });
    }
    if (filterPersonTypeIDList.length) {
      filterPersonTypeIDList.forEach((person_type_id) => {
        searchParams.append('person_type_id', `${person_type_id}`);
      });
    }
    if (filterStartDate) {
      searchParams.set('start_date', `${formatDate(filterStartDate)}`);
    }
    if (filterEndDate) {
      searchParams.set('end_date', `${formatDate(filterEndDate)}`);
    }
    setIsLoading(true);
    api
      .get(`/working-time/export`, {
        params: searchParams,
        responseType: 'blob'
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'working-time_export_data.xlsx');
        document.body.appendChild(link);
        link.click();
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    api.get<Company[]>(`/company`).then((res) => {
      const { data } = res;
      setCompanies(data);
    });
    api.get<PersonType[]>(`/person-type`).then((res) => {
      const { data } = res;
      setPersonTypes(data);
    });
  }, []);
  useEffect(() => {
    if (searchKeyword) {
      const timeOut = setTimeout(() => {
        const searchParams = new URLSearchParams();
        searchParams.set('company_name', searchKeyword);
        searchParams.set('limit', '10');

        api.get<Company[]>(`/company`, { params: searchParams }).then((res) => {
          const { data } = res;
          setCompanies(data);
        });
      }, 500);
      return () => clearTimeout(timeOut);
    }
  }, [searchKeyword]);
  return (
    <Container>
      <PageHeader text="สรุปชั่วโมงการทำงาน" />
      <Box sx={{ mt: 3 }}>
        {/* <ChartSummary
          filterID={filterID}
          filterName={filterName}
          filterCompanyIDList={filterCompanyIDList}
          filterPersonTypeIDList={filterPersonTypeIDList}
          filterStartDate={filterStartDate}
          filterEndDate={filterEndDate}
        /> */}
        <Paper sx={{ my: 2 }}>
          <Box display="flex" justifyContent="space-between" sx={{ p: 2, pb: 0 }}>
            <Typography variant="h6" display="inline" sx={{ py: 1 }}>
              ชั่วโมงการทำงาน
            </Typography>
            <Button variant="outlined" onClick={handleExportCSVFile} disabled={isLoading}>
              Export
            </Button>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2} sx={{ p: 2 }}>
              <Grid item md={3}>
                <TextField
                  id="search-text-label"
                  label="ค้นหาเลขบัตรประชาชน"
                  variant="outlined"
                  placeholder="ค้นหาเลขบัตรประชาชน"
                  onChange={(event) => setFilterID(event.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                  autoComplete="off"
                />
              </Grid>
              <Grid item md={3}>
                <TextField
                  id="search-text-label"
                  label="ค้นหาชื่อ - นามสกุล"
                  variant="outlined"
                  placeholder="ค้นหาชื่อ - นามสกุล"
                  onChange={(event) => setFilterName(event.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                  autoComplete={'off'}
                />
              </Grid>
              <Grid item md={3}>
                <MultiSelect
                  options={personTypes.map((data) => ({
                    id: data.id.toString(),
                    name: data.type_name
                  }))}
                  onChange={(data) => setFilterPersonTypeIDList(data)}
                  label="ประเภทบุคคล"
                />
              </Grid>
              <Grid item md={3}>
                <Autocomplete
                  multiple
                  limitTags={2}
                  disablePortal
                  options={companies}
                  getOptionLabel={(option) => option.company_name}
                  renderInput={(params) => <TextField {...params} label="บริษัท" fullWidth />}
                  renderOption={(prop, option) => (
                    <li {...prop} key={option.id}>
                      {option.company_name}
                    </li>
                  )}
                  onChange={(_, value) =>
                    setFilterCompanyIDList(value.map((data) => data.id.toString()))
                  }
                  onInputChange={(_, newInputValue) => {
                    setSearchKeyword(newInputValue);
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  fullWidth
                />
              </Grid>
              <Grid item md={3}>
                <DesktopDatePicker
                  label="วันที่เริ่มต้น"
                  inputFormat="dd/MM/yyyy"
                  value={filterStartDate}
                  onChange={(value) => setFilterStartDate(value)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item md={3}>
                <DesktopDatePicker
                  label="วันที่สิ้นสุด"
                  inputFormat="dd/MM/yyyy"
                  value={filterEndDate}
                  onChange={(value) => setFilterEndDate(value)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Paper>
        <TableSummaryWorkingTime
          filterID={filterID}
          filterName={filterName}
          filterCompanyIDList={filterCompanyIDList}
          filterPersonTypeIDList={filterPersonTypeIDList}
          filterStartDate={filterStartDate}
          filterEndDate={filterEndDate}
        />
      </Box>
    </Container>
  );
};

export default SummaryWorkingTime;

const Container = styled('div')`
  padding: 16px;
`;
